import { Vue, Component, Watch } from 'vue-property-decorator';
import { Debounce } from '@gig/magic-common';
import type { ISubscription } from '@gig/magic-message-bus/dist/base-bus';
import {
    GAME_CMS_QUERY,
    GAMES_CURRENT_GAME_POSITION_UPDATED,
    GAMES_CURRENT_GAME_UPDATED,
    GAMES_SET_CURRENT_GAME_SLUG,
    USER_LOGGED_IN_UPDATED,
} from '@gig/magic-messages';
import type { ICMSGame, IGame } from '@gig/magic-sdk/dist/adapter/games/game';

@Component
export default class ContinuePlaying extends Vue {
    game: ICMSGame | null = null;

    private resizeListener: (() => void) | null = null;

    private loggedInSubscription: ISubscription | undefined;
    private gameSubscription: ISubscription | undefined;

    get gameRatio() {
        if (!this.game || !this.game.dimensions) {
            return '';
        }
        const { width, height } = this.game.dimensions;
        return `${width}x${height}`;
    }

    get isVisible() {
        return !!this.game && this.$route.name !== 'gameplay';
    }

    created() {
        this.resizeListener = this.adaptIframePosition.bind(this);
        window.addEventListener('resize', this.resizeListener);

        this.loggedInSubscription = window.eventBus.subscribe<boolean>(
            USER_LOGGED_IN_UPDATED,
            () => {
                this.closeGame();
            },
        );

        this.gameSubscription = window.eventBus.subscribe<IGame | null>(
            GAMES_CURRENT_GAME_UPDATED,
            async (event) => {
                const game = event.payload!;
                if (!game) {
                    this.game = null;
                    return;
                }
                this.game = await window.queryBus.query<string, ICMSGame>(
                    GAME_CMS_QUERY,
                    game.slug,
                );
            },
            { replay: true },
        );
    }

    destroyed() {
        this.loggedInSubscription?.unsubscribe();

        if (this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener);
        }

        this.gameSubscription?.unsubscribe();
    }

    hide() {
        this.setCurrentGame(null);
        this.resetCurrentGamePosition();
    }

    private resetCurrentGamePosition() {
        window.eventBus.publish(GAMES_CURRENT_GAME_POSITION_UPDATED, null);
    }

    private getCurrentGameFrame() {
        return document.getElementById('current-game-frame');
    }

    /**
     * Removes internal animation from game frame to avoid conflict with scale
     * animation. It's delayed by duration of the minimize animation
     */
    @Debounce(300)
    private removeFrameAnimation() {
        const frame = this.getCurrentGameFrame();
        if (frame) {
            frame.style.setProperty('--magic-duration', `0s`);
        }
    }

    /**
     * Restores game frame animation for maximizing the frame
     */
    private restoreFrameAnimation() {
        const frame = this.getCurrentGameFrame();
        if (frame) {
            frame.style.removeProperty('--magic-duration');
        }
    }

    /**
     * Set game frame scale in order to fit in the widget when minimizing
     *
     * @param scale Scale factor
     */
    private setFrameScale(scale: number) {
        const frame = this.getCurrentGameFrame();
        if (frame) {
            frame.style.setProperty('--magic-game-frame-scale', `${scale}`);
        }
    }

    /**
     * Removes scaling when game is maximized for gameplay
     */
    private removeFrameScale() {
        const frame = this.getCurrentGameFrame();
        if (frame) {
            frame.style.removeProperty('--magic-game-frame-scale');
        }
    }

    @Watch('isVisible')
    private async adaptIframePosition() {
        const frame = this.getCurrentGameFrame();

        if (!frame) {
            return;
        }

        if (!this.isVisible) {
            this.restoreFrameAnimation();
            this.removeFrameScale();
            return;
        }

        this.removeFrameAnimation();

        // wait for iframe placeholder to render
        await this.$nextTick();

        const frameContainer = this.$refs.slot as HTMLElement;

        this.setFrameScale(frameContainer.clientWidth / frame.clientWidth);
    }

    private closeGame() {
        // remove any game reference when user logs in or logs out.
        // This is (I assume) to avoid passing from free game to money game without noticing it.
        // This is however relevant only in case the component is actually in use.
        if (this.isVisible) {
            this.setCurrentGame(null);
        }
    }

    private setCurrentGame(slug: string | null) {
        window.commandBus.publish(GAMES_SET_CURRENT_GAME_SLUG, slug);
    }
}
